import { computed, onMounted, onUnmounted, watch } from "vue";
import { setKioskEmployeeId } from "@wkt/lib/auth-context";
import { router } from "@wkt/router";
import { useSessionCatalogStore } from "@wkt/stores/sessionCatalog";
import { useShellStore } from "@wkt/stores/shell";
let kioskIntervalId: number | null = null;

export function useKioskMode() {
  const shellStore = useShellStore();
  const sessionCatalogStore = useSessionCatalogStore();

  const isKioskMode = computed(() => shellStore.isKioskSession);

  function stopKioskTimer(): void {
    if (kioskIntervalId !== null) {
      window.clearInterval(kioskIntervalId);
      kioskIntervalId = null;
    }
  }

  function startKioskTimer(): void {
    if (typeof window === "undefined" || kioskIntervalId !== null) {
      return;
    }

    shellStore.resetKioskTimeout();
    kioskIntervalId = window.setInterval(() => {
      if (!shellStore.isKioskSession) {
        stopKioskTimer();
        return;
      }

      shellStore.decrementKioskTimeout();
      if (shellStore.kioskTimeout <= 0) {
        stopKioskTimer();
        exitKiosk();
      }
    }, 1000);
  }

  function resetKioskTimeout(): void {
    if (!shellStore.isKioskSession) {
      return;
    }

    shellStore.resetKioskTimeout();
  }

  function syncKioskRuntime(): void {
    shellStore.syncKioskFromStorage();

    if (shellStore.isKioskSession) {
      startKioskTimer();
      return;
    }

    stopKioskTimer();
  }

  function exitKiosk(): void {
    stopKioskTimer();
    shellStore.setActiveKioskEmployee(null);
    setKioskEmployeeId(null);
    sessionCatalogStore.clearSession({ preserveClientCompany: true });
    void router.replace({ name: "kiosk" }).catch(() => undefined);
  }

  onMounted(() => {
    syncKioskRuntime();
  });

  watch(
    isKioskMode,
    (enabled) => {
      if (enabled) {
        startKioskTimer();
        return;
      }

      stopKioskTimer();
    },
    { flush: "post" },
  );

  onUnmounted(() => {
    stopKioskTimer();
  });

  return {
    isKioskMode,
    resetKioskTimeout,
    startKioskTimer,
    stopKioskTimer,
    syncKioskRuntime,
    exitKiosk,
  };
}
